<template>
  <div class="tab-content">
    <div class="content-scroll-inner mx-pd-15" style="width:750px">
      <div class="flex-container panel-head align-top">
        <div class="flex-item">
          <p v-if="indent.indent_date">
            {{$t('INDENT_INDENT_DATE')}} :
            <strong>{{indent.indent_date | moment('DD MMM YYYY')}}</strong>
          </p>
          <span v-if="indent.material_summary">
            <p>
              {{$t('INDENT_TOTAL_QUANTITY')}} :
              <strong v-if="getQuantityUnits.length">
                <span v-for="(unit, index) in getQuantityUnits" :key="index">
                  {{fixTwoDigits(indent.material_summary.total_quantity[unit])}} {{unit}}
                </span>
              </strong>
              <strong v-else> NA </strong>
            </p>
            <p>
              {{$t('INDENT_TOTAL_MATERIAL_WEIGHT')}} :
              <strong>{{fixTwoDigits(indent.material_summary.total_weight) || 'NA'}}
                {{indent.material_summary.weight_unit}}</strong>
            </p>
            <p v-if="indent && indent.shipment_number">
              {{$t('INDENT_SHIPMENT_NUMBER')}} :
              <strong>{{indent.shipment_number}}</strong>
            </p>
            <p v-if="indent && indent.bid_rate">
              {{$t('INDENT_BID_RATE')}} :
              <strong class="text-green-medium">
                ₹ {{indent.bid_rate }}
              </strong>
            </p>
          </span>
          <p
           v-permission="['view_base_freight']" v-if="indent.base_freight">
            {{$t('INDENT_BASE_FREIGHT')}} : <strong class="text-green-medium">
            ₹ {{indent.base_freight.freight_charge && indent.base_freight.freight_charge.toFixed(2) }}
            <span v-if="freightUnit"> / {{freightUnit}} </span>
            </strong>
          </p>
          <p v-if="indent.transfer_type && $hasFeature('open_indent_details')">
            {{$t('INDENT_FIELD_TRANSFER_TYPE')}} :
            <strong>{{ transferTypeLabel }}</strong>
          </p>
          <p v-if="indent.indent_remarks && $hasFeature('open_indent_details')">
            {{$t('BIDS_REMARKS')}} :
            <strong>{{ indent.indent_remarks }}</strong>
          </p>
        </div>
        <div class="flex-item text-right" v-if="indent.vehicle_type_data">
          <p>
            {{$t('INDENT_DELIVERY_TYPE')}} :
            <strong>{{indent.vehicle_type_data.vehicle_type}}
              <template v-if="mode"> - {{mode}}</template>
            </strong>
          </p>
          <p>
            {{$t('INDENT_VEHICLE_TYPE')}} :
            <strong>{{indent.vehicle_type_data.short_form}}</strong>
          </p>
          <span v-if="indent.material_summary">
            <p>
              {{$t('INDENT_TOTAL_MATERIAL_VOLUME')}} :
              <strong>{{getConvertedClientVolume(fixTwoDigits(indent.material_summary.total_volume)) || 'NA'}}
                {{preferredVolumeUnit}}</strong>
            </p>
          </span>
          <p v-if="indent && indent.ptl_info && indent.ptl_info.no_of_materials">
            {{$t('INDENT_NO_OF_MATERIALS')}} :
            <strong>{{indent.ptl_info.no_of_materials}}</strong>
          </p>
          <p v-if="indent && indent.ptl_info && indent.ptl_info.material_weight">
            {{$t('INDENT_WEIGHT_KG')}} :
            <strong>{{indent.ptl_info.material_weight}} KG</strong>
          </p>
          <p v-if="indent && indent.ptl_info && indent.ptl_info.material_volume">
            {{$t('INDENT_VOLUME_TYPE')}} :
            <strong>{{getConvertedClientVolume(indent.ptl_info.material_volume)}} {{preferredVolumeUnit}}</strong>
          </p>
        </div>
      </div>
      <collapse accordion class="collapse-slider mx-15" v-model="activeName">
      <collapse-item title="Other Information" v-if="hasCustomFields || hasOtherDocuments">
        <CustomField v-if="hasCustomFields"
          :currentIndent="indent"
          :indentCustomFields="indentCustomFields"
          :indentSourceCustomFields="indentSourceCustomFields"
          :indentDestinationCustomFields="indentDestinationCustomFields"
          :customFields="indent.custom_fields"
          :isTransporter=true />
        <div v-if="hasOtherDocuments" class="mt-10">
          <div class="popover-doc">
            <label>{{ $t('INDENT_OTHER_ATTACHMENT') }}</label>
            <Attachments
              :disableUpload="true"
              :attachments="otherDocuments"
              :showPreview="true"
              :disableDelete="true"
            />
          </div>
        </div>
       </collapse-item>
        <collapse-item :name="index" v-for="(pickup,index) in getIndentDetails" :key="index">
          <template slot="title">
            <span v-if="pickup.gateName">
              {{pickup.gateName}}
              <i class="el-icon-minus icon-title"></i>
            </span>
            <span class="text-normal">{{pickup.name}}</span>
          </template>
          <div class="flex-container panel-head align-top">
            <div class="flex-item">
              <p>
                {{$t('INDENT_ADDRESS')}} :
                <strong>{{pickup.address}}</strong>
              </p>
              <p>
                {{$t('INDENT_DOCKS')}} :
                <strong> <Ellipsis :text="pickup.docksName" :length="20" /> </strong>
              </p>
              <p v-if="$hasPermission('view_contact_details')">
                {{$t('INDENT_CONTACT_DETAILS')}} :
                <strong>{{pickup.phone ? pickup.phone : 'NA'}}</strong>
              </p>
            </div>
            <div class="flex-item text-right">
              <p>
                {{$t('INDENT_REFERENCE_ID')}} :
                <strong>{{pickup.refId}}</strong>
              </p>
              <p>
                {{$t('INDENT_VEHICLE_REQUIREMENT_DATE')}}:
                <strong>{{pickup.vehicleReqDate}}</strong>
              </p>
            </div>
          </div>
          <Table
            align="top"
            class="table-striped header-word-break bordered mt-15"
            max-height="250px"
            :data="pickup.drops"
            :stripe="true"
            aria-describedby="indentDetailsData"
            >
            <TableColumn :label="$t('INDENT_SHIPPED_TO')" width="170">
              <template slot-scope="scope">
                <span class="d-block">{{scope.row.shipToName}}</span>
                <span class="text-xs">{{ `${scope.row.shipToRefId} - ${scope.row.shipToGate} - ${scope.row.shipToCity}` }}</span>
              </template>
            </TableColumn>
            <TableColumn prop="shipToAddress" :label="$t('INDENT_ADDRESS')" >
            </TableColumn>
            <TableColumn prop="mobile" :label="$t('INDENT_CONTACT_DETAILS')" v-if="$hasPermission('view_contact_details')" width="120"></TableColumn>
            <TableColumn :label="$t('INDENT_BOOKING_DATE')" v-if="$hasFeature('enable_booking_date')" width="120">
              <template slot-scope="scope">
                <span class="d-block">
                  {{scope.row.bookingDay}}
                  <br>
                  {{scope.row.bookingDate}}
                  <br>
                  {{scope.row.bookingTime}}
                </span>
              </template>
            </TableColumn>
            <TableColumn :label="$t('INDENT_APPOINTMENT_ID')" v-if="$hasFeature('enable_appointment_id')" width="120">
              <template slot-scope="scope">
                <span class="d-block">{{scope.row.appointmentId}}</span>
              </template>
            </TableColumn>
            <TableColumn :label="$t(expectedDate)" width="140">
              <template slot-scope="scope">
                <span class="d-block">
                  {{scope.row.expectedDeliveryDay}}
                  <br>
                  {{scope.row.expectedDeliveryDate}}
                  <br>
                  <div v-if="deliveryWindow" class="text-xs">
                    <span>{{getFirstWindow(scope.row.delivery_window)}}</span>
                    <Tooltip v-if="getDeliveryWindow(scope.row.delivery_window)" class="item" effect="dark" placement="bottom" :open-delay="300">
                        <div slot="content" class="jumbotron tooltipcontent">
                          {{getExceptFirstWindow(scope.row.delivery_window)}}
                        </div>
                        <span class="text-tag">{{getDeliveryWindow(scope.row.delivery_window)}}</span>
                    </Tooltip>
                  </div>
                </span>
              </template>
            </TableColumn>
            <TableColumn prop="otp" :label="$t('INDENT_OTP')" width="80">
            </TableColumn>
          </Table>
        </collapse-item>
        <CollapseItem :title="$t('INDENT_WEIGHTS')" v-if="$hasFeature('truck_weights')">
          <Form :inline="true" class="form-default form-triple" :disabled="true">
            <div class="item-group">
              <FormItem :label="$t('INDENT_TARE_WEIGHT')">
                <Input
                 type="number"
                 v-model.number="localTareWeight"
                 size="small"
                 :disabled="true"
               />
              </FormItem>
              <FormItem :label="$t('INDENT_GROSS_WEIGHT')">
              <Input
                 type="number"
                 v-model.number="localGrossWeight"
                 size="small"
                 required
                 :disabled="true"
               />
             </FormItem>
            </div>
           </Form>
        </CollapseItem>
      </collapse>
    </div>
  </div>
</template>
<script>
/*  eslint-disable import/no-extraneous-dependencies */
import { mapGetters } from 'vuex';
import { vehicleTypeMode, IndentTransferType } from 'pando-constants';
import { invert } from 'lodash';
import moment from 'moment';
import getAddress from '@/lib/Address';
import CustomField from '@common/components/CustomField';
import Attachments from '@common/components/Attachments';
import { contractTypes } from '@/constants/indentConstants';
import VolumeFormulaConvertor from '../../../../../../common-v2/helpers/volume-formula-converter';

export default {
  props: {
    indent: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      activeName: 0,
      invertedVehicleTypeMode: invert(vehicleTypeMode),
      expectedDate: '',
      localTareWeight: this.indent && this.indent.tare_weight,
      localGrossWeight: this.indent && this.indent.gross_weight,
      otherDocuments: [],
    };
  },
  components: {
    CustomField,
    Attachments,
  },
  created() {
    this.expectedDate = this.deliveryWindow ? 'INDENT_DELIVERY_WINDOW' : 'INDENT_EXPECTED_DATE_TIME';
  },
  watch: {
    indent: {
      handler(val) {
        if (val && val.other_attachments && val.other_attachments.length) {
          this.otherDocuments = val.other_attachments
            .map(doc => ({
              id: doc.id,
              name: doc.name,
              url: doc.url,
            }));
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters({
      indentCustomFields: 'app/indentCustomFields',
      indentSourceCustomFields: 'app/indentSourceCustomFields',
      indentDestinationCustomFields: 'app/indentDestinationCustomFields',
      mdmBoostrap: 'app/mdmBoostrap',
    }),
    preferredVolumeUnit() {
      return this.$preferredVolumeUnit();
    },
    hasCustomFields() {
      return (this.indentCustomFields && this.indentCustomFields.length) ||
        (this.indentSourceCustomFields && this.indentSourceCustomFields.length) ||
        (this.indentDestinationCustomFields && this.indentDestinationCustomFields.length);
    },
    hasOtherAttachmentPermission() {
      return this.$hasPermission('indent_view_other_attachment');
    },
    hasOtherDocuments() {
      return this.otherDocuments && this.otherDocuments.length > 0;
    },
    deliveryWindow() {
      return !!((this.mdmBoostrap &&
      this.mdmBoostrap.consignee && this.mdmBoostrap.consignee.length > 0));
    },
    hasDocks() {
      return this.$hasFeature('docks');
    },
    freightUnit() {
      const currUnit = this.indent.base_freight.freight_unit;
      let unit = contractTypes[currUnit - 1] && contractTypes[currUnit - 1].name;
      // remove displaying per km in the UI
      if (currUnit === 8) {
        unit = '';
      }
      return unit;
    },
    mode() {
      return this.invertedVehicleTypeMode[this.indent.vehicle_type_data.mode];
    },
    getIndentDetails() {
      return this.indent.sources.map((source) => {
        let drops = this.indent.destinations.filter((drop) => {
          if (drop.source_gate_id) {
            return drop.source_gate_id === source.gate_id;
          }
          return drop.source_location_id === source.location_id;
        });
        drops = drops.map((element) => {
          const drop = element;
          let shipToAddress = getAddress(drop.address);
          let shipToRefId = drop.address.reference_id;
          let shipToCity = drop.address.city;
          let shipToGate = drop.address.name;
          if (drop.address.gates) {
            // eslint-disable-next-line no-underscore-dangle
            const gate = drop.address.gates.find(gateAdd => gateAdd._id === drop.gate_id);
            shipToAddress = getAddress(gate, true);
            shipToRefId = gate.reference_id;
            shipToCity = gate.city;
            shipToGate = gate.name
          }
          return {
            shipToName: drop.address.name,
            shipToGate,
            shipToRefId,
            shipToAddress,
            shipToCity,
            unloaderAvailable: drop.unloader_available ? 'Yes' : 'No',
            addToInvoice: drop.add_to_invoice ? 'Yes' : 'No',
            ...(drop.expected_delivery_at) ? {
              expectedDeliveryDay: moment(drop.expected_delivery_at)
                .format('dddd'),
              expectedDeliveryDate: moment(drop.expected_delivery_at)
                .format('DD MMM YY'),
            } : { expectedDeliveryDay: '-' },
            ...(drop.booking_date) ? {
              bookingDay: moment(drop.booking_date).format('dddd'),
              bookingDate: moment(drop.booking_date).format('DD MMM YY'),
              bookingTime: moment(drop.booking_date).format('hh:mm A'),
            } : { bookingDay: '-', bookingDate: '-', bookingTime: '-' },
            appointmentId: drop.appointment_id || '-',
            delivery_window: drop.delivery_window,
            otp: drop.otp || 'NA',
            mobile: (drop.address.phone || drop.address.rmobile) || 'NA',
          };
        });
        let refId = source.address.reference_id;
        let gate = null;
        let sourceCity;
        if (source.address.gates) {
          // eslint-disable-next-line no-underscore-dangle
          gate = source.address.gates.find(gateAdd => gateAdd && gateAdd._id === source.gate_id);
          refId = gate.reference_id;
          sourceCity = gate.city;
        }
        return {
          gateName: gate ? gate.name : '',
          address: gate ? gate.address : source.address.address,
          name: source.address.name,
          refId,
          sourceCity,
          loaderAvailable: source.loader_available ? 'Available' : 'Not available',
          vehicleReqDate: moment(source.vehicle_req_date).format('DD MMM YYYY'),
          addToInvoice: source.add_to_invoice ? 'Add to invoice' : 'Don\'t add to invoice',
          docksName: source.docks ? (source.docks).reduce((acc, dock) => (acc ? `${acc}, ${dock.name}` : dock.name), '') : '-',
          drops,
          phone: (source.address.phone || source.address.rmobile) || 'NA',
        };
      });
    },
    getQuantityUnits() {
      const qtyUnits = [];
      const materialSummary = this.indent.material_summary;
      if (materialSummary && materialSummary.total_quantity) {
        const keys = Object.keys(materialSummary.total_quantity);
        keys.forEach((key) => {
          if (materialSummary.total_quantity[key]) {
            qtyUnits.push(key);
          }
        });
      }
      return qtyUnits;
    },
    transferTypeLabel() {
      const transferTypeId = this.indent.transfer_type;
      if (!transferTypeId || !IndentTransferType) {
        return '\u2014';
      }
      const transferTypeMap = {
        [IndentTransferType.REGULAR]: 'TRANSFER_TYPE_REGULAR',
        [IndentTransferType.OUT_OF_METRICS]: 'TRANSFER_TYPE_OUT_OF_METRICS',
        [IndentTransferType.DC_TO_DC]: 'TRANSFER_TYPE_DC_TO_DC',
        [IndentTransferType.DESTRUCTION]: 'TRANSFER_TYPE_DESTRUCTION',
      };
      const translationKey = transferTypeMap[transferTypeId];
      return translationKey ? this.$t(translationKey) : '\u2014';
    },
  },
  methods: {
    fixTwoDigits(val) {
      if (val) {
        const value = Number(val);
        return parseFloat(value.toFixed(2));
      }
      return val;
    },
    getFirstWindow(str) {
      if (str) {
        const substr = str.split(',');
        return substr[0];
      }
      return '';
    },
    getExceptFirstWindow(str) {
      if (str) {
        const substr = str.split(',');
        substr.splice(0, 1);
        return substr.join(',');
      }
      return '';
    },
    getDeliveryWindow(str) {
      if (str && str.indexOf(',') > 0) {
        const substr = str.split(',');
        const remainingCount = substr.length - 1;
        return `+${remainingCount}`;
      }
      return '';
    },
    getConvertedClientVolume(volume) {
      const volumeInClientUnit = new VolumeFormulaConvertor().revertToClient(this.preferredVolumeUnit, volume);
      return volumeInClientUnit;
    },
  },
};

</script>

<style>
.item-group {
  display: flex;
  gap: 20px;
}
.custom-dropdown-width {
  max-width: 350px;
  width: 100%;
}
</style>
